import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Box,
    Button,
    Heading,
    Input,
    SimpleGrid,
    Spinner,
    Text,
    useToast
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import SignaturePad from 'react-signature-canvas';
import { getPatientByValidationToken, submitPatientValidation } from '../../../api';
import Logo from '../../../template/HomePage/components/Logo';

const PatientValidationPage = () => {
    const { token } = useParams();
    const toast = useToast();
    const sigPadRef = useRef();
    const confirmRef = useRef();

    const [loading, setLoading] = useState(true);
    const [patient, setPatient] = useState(null);
    const [needs, setNeeds] = useState({ requestId: false, requestIC: false, idExpired: false });
    const [submitted, setSubmitted] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // previews
    const [idFrontPreview, setIdFrontPreview] = useState(null);
    const [idBackPreview, setIdBackPreview] = useState(null);
    const [icFrontPreview, setIcFrontPreview] = useState(null);
    const [icBackPreview, setIcBackPreview] = useState(null);

    // files
    const [idFrontFile, setIdFrontFile] = useState(null);
    const [idBackFile, setIdBackFile] = useState(null);
    const [icFrontFile, setIcFrontFile] = useState(null);
    const [icBackFile, setIcBackFile] = useState(null);

    useEffect(() => {
        const fetchPatient = async () => {
            const res = await getPatientByValidationToken(token);
            if (res.success) {
                setPatient(res.data.patient);
                setNeeds(res.data.needs);
            } else {
                toast({
                    title: 'Invalid or expired link',
                    description: res.message || '',
                    status: 'error',
                    duration: 4000,
                    isClosable: true
                });
            }
            setLoading(false);
        };
        fetchPatient();
    }, [token, toast]);

    const getUserAgentAndIP = async () => {
        const userAgent = navigator.userAgent;
        try {
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 3500);
            const res = await fetch('https://api.ipify.org?format=json', { signal: controller.signal });
            clearTimeout(timeout);
            const data = await res.json();
            return { ip: data.ip, agent: userAgent };
        } catch {
            return { ip: null, agent: userAgent };
        }
    };

    const handleFileChange = (e, setPreview, setFile) => {
        if (e.target.files[0]) {
            setPreview(URL.createObjectURL(e.target.files[0]));
            setFile(e.target.files[0]);
        }
    };

    const handleSubmit = () => {
        // signature check
        if (sigPadRef.current.isEmpty()) {
            toast({
                title: 'Signature Required',
                description: 'Please sign before submitting.',
                status: 'warning',
                duration: 3000,
                isClosable: true
            });
            return;
        }

        // required file checks
        if (needs.requestId) {
            if (!idFrontFile || !idBackFile) {
                toast({
                    title: 'Missing ID/DL images',
                    description: 'Please upload both front and back images of your ID/DL.',
                    status: 'warning',
                    duration: 3000,
                    isClosable: true
                });
                return;
            }
        }

        if (needs.requestIC) {
            if (!icFrontFile || !icBackFile) {
                toast({
                    title: 'Missing insurance card images',
                    description: 'Please upload both front and back images of your insurance card.',
                    status: 'warning',
                    duration: 3000,
                    isClosable: true
                });
                return;
            }
        }

        setIsConfirmOpen(true);
    };

    const confirmSubmit = async () => {
        setSubmitting(true);
        const userMetadata = await getUserAgentAndIP();

        const formData = new FormData();

        // signature & metadata
        formData.append('image', sigPadRef.current.getTrimmedCanvas().toDataURL('image/png'));
        formData.append('ip', userMetadata.ip || '');
        formData.append('agent', userMetadata.agent || '');

        // files + docTypes[] (field name must match backend: "documents")
        if (idFrontFile) {
            formData.append('documents', idFrontFile);
            formData.append('docTypes[]', 'idFront');
        }
        if (idBackFile) {
            formData.append('documents', idBackFile);
            formData.append('docTypes[]', 'idBack');
        }
        if (icFrontFile) {
            formData.append('documents', icFrontFile);
            formData.append('docTypes[]', 'insFront');
        }
        if (icBackFile) {
            formData.append('documents', icBackFile);
            formData.append('docTypes[]', 'insBack');
        }

        const res = await submitPatientValidation(token, formData);
        setSubmitting(false);

        if (res.success) {
            setSubmitted(true);
        } else {
            toast({
                title: 'Error',
                description: res.message,
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        }
        setIsConfirmOpen(false);
    };


    if (loading) {
        return (
            <>
                <Logo height={44} />
                <Box textAlign="center" mt={20}>
                    <Spinner size="xl" />
                </Box>
            </>
        );
    }

    if (!patient) {
        return (
            <>
                <Logo height={44} />
                <Box textAlign="center" py={10} px={6}>
                    <Heading fontWeight="light" mb={4}>Broken Link</Heading>
                    <Text>The link you followed is not valid anymore or patient not found.</Text>
                    <Text mt={2}>
                        If you believe this is a mistake, contact us at <b>(888) 882-3330</b> or <b>CS@MYDLSLAB.COM</b>
                    </Text>
                </Box>
            </>
        );
    }

    if (submitted) {
        return (
            <>
                <Logo height={44} />
                <Box textAlign="center" py={20} px={6}>
                    <Heading color="green.500" mb={4}>Thank You!</Heading>
                    <Text fontSize="lg" mb={2}>
                        Your validation has been successfully submitted.
                    </Text>
                    <Text fontSize="md" color="gray.600">
                        You can now safely close this page.
                    </Text>
                </Box>
            </>
        );
    }

    return (
        <>
            <Logo height={44} />
            <Box maxW="700px" mx="auto" p={8} borderWidth="1px" borderRadius="lg" boxShadow="md" bg="white">
                <Heading mb={4} fontSize="2xl" textAlign="center" color="blue.700">
                    Patient Validation
                </Heading>

                <Box mb={6} borderWidth="1px" borderRadius="md" p={5} bg="gray.50">
                    <Text fontWeight="bold" color="gray.600">
                        Name: {patient.firstName} {patient.lastName}
                    </Text>
                </Box>

                {(needs.requestId || needs.requestIC || needs.idExpired) && (
                    <Box bg="yellow.50" border="1px solid" borderColor="yellow.200" p={4} borderRadius="md" mb={6}>
                        <Text fontWeight="medium" color="yellow.800">
                            Our records indicate that your patient file:
                        </Text>
                        <Box as="ul" pl={5} mt={2} color="yellow.800">
                            {needs.requestId && <li>Missing Photo ID/DL</li>}
                            {needs.requestIC && <li>Missing Insurance Card</li>}
                            {needs.idExpired && <li>Has an expired Photo ID/DL</li>}
                        </Box>
                    </Box>
                )}

                {needs.requestId && (
                    <>
                        <Heading size="sm" mb={2}>Photo ID/DL</Heading>
                        <SimpleGrid columns={[1, 2]} spacing={4} mb={4}>
                            <Box as="label" htmlFor="idFrontUpload" {...uploadBoxStyles}>
                                {idFrontPreview ? <img src={idFrontPreview} alt="ID Front" style={imgStyle} /> : <UploadBoxLabel text="DL/ID Front" />}
                                <Input id="idFrontUpload" type="file" accept="image/*" display="none"
                                    onChange={(e) => handleFileChange(e, setIdFrontPreview, setIdFrontFile)} />
                            </Box>
                            <Box as="label" htmlFor="idBackUpload" {...uploadBoxStyles}>
                                {idBackPreview ? <img src={idBackPreview} alt="ID Back" style={imgStyle} /> : <UploadBoxLabel text="DL/ID Back" />}
                                <Input id="idBackUpload" type="file" accept="image/*" display="none"
                                    onChange={(e) => handleFileChange(e, setIdBackPreview, setIdBackFile)} />
                            </Box>
                        </SimpleGrid>
                    </>
                )}

                {needs.requestIC && (
                    <>
                        <Heading size="sm" mb={2}>Insurance Card</Heading>
                        <SimpleGrid columns={[1, 2]} spacing={4} mb={4}>
                            <Box as="label" htmlFor="icFrontUpload" {...uploadBoxStyles}>
                                {icFrontPreview ? <img src={icFrontPreview} alt="Insurance Front" style={imgStyle} /> : <UploadBoxLabel text="Insurance Front" />}
                                <Input id="icFrontUpload" type="file" accept="image/*" display="none"
                                    onChange={(e) => handleFileChange(e, setIcFrontPreview, setIcFrontFile)} />
                            </Box>
                            <Box as="label" htmlFor="icBackUpload" {...uploadBoxStyles}>
                                {icBackPreview ? <img src={icBackPreview} alt="Insurance Back" style={imgStyle} /> : <UploadBoxLabel text="Insurance Back" />}
                                <Input id="icBackUpload" type="file" accept="image/*" display="none"
                                    onChange={(e) => handleFileChange(e, setIcBackPreview, setIcBackFile)} />
                            </Box>
                        </SimpleGrid>
                    </>
                )}

                <Box mt={8} borderTop="3px solid #ccc" pt={5}>
                    <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={6} textAlign="center">
                        Please sign below to confirm.
                    </Text>
                    <Text mb={2} fontWeight="medium">Your Digital Signature:</Text>
                    <Box mt={6} mb={6} fontSize="sm" color="gray.600">
                        <Text>
                            By signing below, you confirm that all the information shown is accurate and you are authorized to sign.
                        </Text>
                    </Box>
                    <Text color="orange.600" fontSize="sm">Please sign within the box borders</Text>
                    <Box border="1px solid #ccc" borderRadius="md" bg="white" w="100%" overflow="hidden" mt={2}>
                        <SignaturePad ref={sigPadRef} canvasProps={{ height: 250, style: { width: '100%', display: 'block' } }} />
                    </Box>
                    <Button mt={2} size="xs" colorScheme="pink" onClick={() => sigPadRef.current.clear()}>
                        Clear Signature
                    </Button>
                </Box>

                <Button mt={6} colorScheme="green" size="md" width="100%" onClick={handleSubmit}>
                    Submit Signature
                </Button>

                <AlertDialog isOpen={isConfirmOpen} leastDestructiveRef={confirmRef} onClose={() => setIsConfirmOpen(false)}>
                    <AlertDialogOverlay>
                        <AlertDialogContent>
                            <AlertDialogHeader>Confirm Submission</AlertDialogHeader>
                            <AlertDialogBody>
                                Are you sure you want to submit your signature? You won't be able to edit it after submission.
                            </AlertDialogBody>
                            <AlertDialogFooter>
                                <Button ref={confirmRef} onClick={() => setIsConfirmOpen(false)}>Cancel</Button>
                                <Button colorScheme="green" isLoading={submitting} onClick={confirmSubmit} ml={3}>
                                    Yes, Submit
                                </Button>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialogOverlay>
                </AlertDialog>
            </Box>
        </>
    );
};

const uploadBoxStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px dashed',
    borderColor: 'gray.300',
    borderRadius: 'md',
    h: '200px',
    cursor: 'pointer',
    _hover: { borderColor: 'blue.400', bg: 'blue.50' }
};

const imgStyle = { maxHeight: '100%', maxWidth: '100%' };

const UploadBoxLabel = ({ text }) => (
    <>
        <Text fontSize="4xl" color="gray.400" mb={2}>+</Text>
        <Text fontWeight="medium" color="gray.600">{text}</Text>
    </>
);

export default PatientValidationPage;
